// FT-220 CTR-02: единственная точка входа для /api/v1/damage_control/*.
// DI axios сохраняет интерсепторы приложения и позволяет использовать ручной мок в тестах.
import { reportModuleFailure } from "@/utils/moduleFailure";

const BASE = "/api/v1/damage_control";
const ERROR_KEYS = Object.freeze({
  forbidden: "damage_control.errors.forbidden",
  damage_control_not_in_plan: "damage_control.errors.damage_control_not_in_plan",
  damage_control_disabled: "damage_control.errors.damage_control_disabled",
  damage_control_no_template: "damage_control.errors.damage_control_no_template",
  validation_failed: "damage_control.errors.validation_failed",
  conflict: "damage_control.errors.conflict",
  not_found: "damage_control.errors.not_found",
  locked: "damage_control.errors.locked",
  storage_unavailable: "damage_control.errors.storage_unavailable",
  staff_only: "damage_control.errors.staff_only",
});
export const DAMAGE_CONTROL_ERROR_CODES = Object.freeze(Object.keys(ERROR_KEYS));

const object = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const unwrap = (promise) => promise.then((response) => response.data);
const segment = (value) => encodeURIComponent(value);

// Обе формы отказа: старое строковое error и новый объект операции. Метаданные
// (zone_key, blocking_inspection_id, details, codes, message) сохраняем, но текст сервера не показываем.
export function parseDamageControlError(error) {
  const response = error && error.response;
  const data = response && response.data;
  if (!object(data)) return null;
  const nested = object(data.error) ? data.error : null;
  const code = nested ? nested.code : data.error;
  if (!DAMAGE_CONTROL_ERROR_CODES.includes(code)) return null;
  const details = { ...data, ...(nested || {}) };
  delete details.error;
  if (nested) delete details.code;
  return { code, status: response.status, details, i18nKey: ERROR_KEYS[code] };
}

// Ожидаемый отказ — локализованная причина без Rollbar; неизвестный сбой —
// общий текст и обработка сети/сессии по образцу reportRentToOwnFailure.
export function reportDamageControlFailure(vm, err, { silent = false } = {}) {
  return reportModuleFailure(vm, err, { text: vm.$t("damage_control.errors.request_failed"), silent, parse: parseDamageControlError });
}

const count = (value) => Number.isInteger(value) && value >= 0 ? value : 0;

// REQ-01/02: только серверный флаг филиала, включая триал. План на клиенте не пересчитываем.
export function damageControlAccess(company) {
  const raw = company && company.damage_control_access;
  const access = object(raw) ? raw : {};
  const trial = object(access.trial) ? access.trial : {};
  return {
    in_plan: access.in_plan === true,
    // Без услуги в плане модуля нет ни в каком виде (USER 2026-10-26), даже если флаг enabled пришёл противоречивым.
    enabled: access.enabled === true && access.in_plan === true,
    strict: access.strict === true,
    trial: { active: trial.active === true, used: count(trial.used), limit: count(trial.limit) },
  };
}

export const damageControlEnabled = (company) => damageControlAccess(company).enabled;
// The module is exposed only to internal staff, and only when the server grants access.
export const damageControlVisible = (company, isStaff) => damageControlEnabled(company) && isStaff === true;
export const damageControlStrict = (company) => damageControlEnabled(company) && damageControlAccess(company).strict;
export const damageControlInPlan = (company) => damageControlAccess(company).in_plan;
export function damageControlTrialLeft(company) {
  const { trial } = damageControlAccess(company);
  return trial.active ? Math.max(0, trial.limit - trial.used) : null;
}

// FT-220 CTR-08: снятие предупреждения (api STEP-40); ответ — payload предупреждения, отметку зоны сбрасывает сервер.
export const dismissWarning = (axios, warningId, reason) =>
  unwrap(axios.post(`${BASE}/warnings/${segment(warningId)}/dismiss`, reason == null ? {} : { reason }));

export const damageControlApi = {
  getSettings: (axios) => unwrap(axios.get(`${BASE}/settings`)),
  updateSettings: (axios, attrs) => unwrap(axios.patch(`${BASE}/settings`, attrs)),
  listTemplates: (axios) => unwrap(axios.get(`${BASE}/templates`)),
  getTemplate: (axios, id) => unwrap(axios.get(`${BASE}/templates/${segment(id)}`)),
  createTemplate: (axios, attrs = {}) => unwrap(axios.post(`${BASE}/templates`, attrs)),
  updateTemplate: (axios, id, attrs) => unwrap(axios.patch(`${BASE}/templates/${segment(id)}`, attrs)),
  resetTemplate: (axios, id) => unwrap(axios.patch(`${BASE}/templates/${segment(id)}/reset`)),
  templateExamplePhotoUpload: (axios, id, attrs) => unwrap(axios.post(`${BASE}/templates/${segment(id)}/example_photo_uploads`, attrs)),
  assignTemplate: (axios, carId, templateId) => unwrap(axios.patch(`${BASE}/cars/${segment(carId)}/template`, { template_id: templateId })),
  startInspection: (axios, bookingId, side) => unwrap(axios.post(`${BASE}/bookings/${segment(bookingId)}/inspections`, { side })),
  getInspection: (axios, id, config) => unwrap(config ? axios.get(`${BASE}/inspections/${segment(id)}`, config) : axios.get(`${BASE}/inspections/${segment(id)}`)),
  reviewZone: (axios, id, zoneKey) => unwrap(axios.post(`${BASE}/inspections/${segment(id)}/zones/${segment(zoneKey)}/review`)),
  completeInspection: (axios, id) => unwrap(axios.post(`${BASE}/inspections/${segment(id)}/complete`)),
  reshootInspection: (axios, id) => unwrap(axios.post(`${BASE}/inspections/${segment(id)}/reshoot`)),
  closeInspection: (axios, id) => unwrap(axios.post(`${BASE}/inspections/${segment(id)}/close`)),
  prepareUpload: (axios, inspectionId, attrs) => unwrap(axios.post(`${BASE}/inspections/${segment(inspectionId)}/shots/upload`, attrs)),
  registerShot: (axios, inspectionId, attrs) => unwrap(axios.post(`${BASE}/inspections/${segment(inspectionId)}/shots`, attrs)),
  drawFinding: (axios, shotId, attrs) => unwrap(axios.post(`${BASE}/shots/${segment(shotId)}/findings`, attrs)),
  rejectFinding: (axios, id) => unwrap(axios.post(`${BASE}/findings/${segment(id)}/reject`)),
  linkKnownFinding: (axios, id, knownRecordId) => unwrap(axios.post(`${BASE}/findings/${segment(id)}/link_known`, { known_record_id: knownRecordId })),
  reclassifyFinding: (axios, id, attrs) => unwrap(axios.post(`${BASE}/findings/${segment(id)}/reclassify`, attrs)),
  resolveStaleFinding: (axios, id, resolution) => unwrap(axios.post(`${BASE}/findings/${segment(id)}/resolve_stale`, { resolution })),
  getPassport: (axios, carId) => unwrap(axios.get(`${BASE}/cars/${segment(carId)}/passport`)),
  markRepaired: (axios, carId, id) => unwrap(axios.post(`${BASE}/cars/${segment(carId)}/passport/${segment(id)}/mark_repaired`)),
  revokePassportEntry: (axios, carId, id) => unwrap(axios.post(`${BASE}/cars/${segment(carId)}/passport/${segment(id)}/revoke`)),
  getAct: (axios, bookingId) => unwrap(axios.get(`${BASE}/bookings/${segment(bookingId)}/act`)),
  getActPdf: (axios, inspectionId) => unwrap(axios.get(`${BASE}/inspections/${segment(inspectionId)}/act_pdf`)),
  dismissWarning,
};
